import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, Plus, Star, Trash2, Edit2, Copy, ArrowLeft,
  Loader2, Save, CheckCircle, Package, Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceTemplatesPage = () => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    default_payment_terms: 30,
    default_notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 20, unit: 'unité' }]
  });

  useEffect(() => {
    loadTemplates();
    loadSuggestions();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/templates/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/templates/suggestions/common`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingTemplate 
        ? `${API_URL}/api/templates/${editingTemplate.id}`
        : `${API_URL}/api/templates/`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast({
          title: editingTemplate ? 'Modèle mis à jour' : 'Modèle créé',
          description: `Le modèle "${formData.name}" a été enregistré.`
        });
        resetForm();
        loadTemplates();
      } else {
        const error = await response.json();
        toast({
          title: 'Erreur',
          description: error.detail || 'Impossible de sauvegarder le modèle',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur de connexion',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Modèle supprimé' });
        loadTemplates();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le modèle',
        variant: 'destructive'
      });
    }
  };

  const handleToggleFavorite = async (template) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_favorite: !template.is_favorite })
      });
      loadTemplates();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category || '',
      default_payment_terms: template.default_payment_terms || 30,
      default_notes: template.default_notes || '',
      items: template.items.length > 0 ? template.items : [{ description: '', quantity: 1, unit_price: 0, vat_rate: 20, unit: 'unité' }]
    });
    setShowCreate(true);
  };

  const handleUseSuggestion = (suggestion) => {
    setFormData({
      name: suggestion.name,
      description: '',
      category: suggestion.category,
      default_payment_terms: 30,
      default_notes: '',
      items: suggestion.items
    });
    setShowCreate(true);
  };

  const resetForm = () => {
    setShowCreate(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      default_payment_terms: 30,
      default_notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 20, unit: 'unité' }]
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, vat_rate: 20, unit: 'unité' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      const ht = item.quantity * item.unit_price;
      const ttc = ht * (1 + item.vat_rate / 100);
      return sum + ttc;
    }, 0);
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="h-6 w-6 text-blue-600" />
              Modèles de factures
            </h1>
            <p className="text-slate-500 text-sm">Créez et réutilisez vos modèles</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau modèle
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showCreate && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">
              {editingTemplate ? 'Modifier le modèle' : 'Créer un modèle'}
            </CardTitle>
            <CardDescription>
              Définissez les lignes pré-remplies pour ce type de facture
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nom du modèle *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Prestation mensuelle"
                    required
                    data-testid="template-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sans catégorie</option>
                    <option value="consulting">Consulting</option>
                    <option value="development">Développement</option>
                    <option value="design">Design</option>
                    <option value="training">Formation</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description (optionnel)
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description du modèle"
                />
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Lignes pré-remplies
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une ligne
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="mb-2"
                        />
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="text-xs text-slate-500">Qté</label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.5"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Prix HT</label>
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">TVA %</label>
                            <select
                              value={item.vat_rate}
                              onChange={(e) => updateItem(index, 'vat_rate', parseFloat(e.target.value))}
                              className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm"
                            >
                              <option value="20">20%</option>
                              <option value="10">10%</option>
                              <option value="5.5">5.5%</option>
                              <option value="0">0%</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Unité</label>
                            <select
                              value={item.unit}
                              onChange={(e) => updateItem(index, 'unit', e.target.value)}
                              className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm"
                            >
                              <option value="unité">Unité</option>
                              <option value="heure">Heure</option>
                              <option value="jour">Jour</option>
                              <option value="mois">Mois</option>
                              <option value="forfait">Forfait</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      {formData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Total preview */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total TTC estimé:</span>
                    <span className="font-semibold text-blue-700">
                      {calculateTotal(formData.items).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Délai de paiement par défaut
                  </label>
                  <select
                    value={formData.default_payment_terms}
                    onChange={(e) => setFormData({...formData, default_payment_terms: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="0">Paiement immédiat</option>
                    <option value="15">15 jours</option>
                    <option value="30">30 jours</option>
                    <option value="45">45 jours</option>
                    <option value="60">60 jours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes par défaut
                  </label>
                  <Input
                    value={formData.default_notes}
                    onChange={(e) => setFormData({...formData, default_notes: e.target.value})}
                    placeholder="Notes à inclure sur la facture"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-template-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Suggestions */}
      {!showCreate && templates.length === 0 && suggestions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Modèles suggérés
            </CardTitle>
            <CardDescription>
              Commencez rapidement avec ces modèles prédéfinis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {suggestions.map((suggestion, idx) => (
                <div 
                  key={idx}
                  onClick={() => handleUseSuggestion(suggestion)}
                  className="p-4 bg-white border border-amber-200 rounded-xl hover:border-amber-400 hover:shadow-sm cursor-pointer transition-all"
                >
                  <h4 className="font-medium text-slate-800 mb-1">{suggestion.name}</h4>
                  <p className="text-xs text-slate-500 mb-2">
                    {suggestion.items.length} ligne(s)
                  </p>
                  <Badge variant="outline" className="text-xs">
                    {suggestion.category}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mes modèles</CardTitle>
          <CardDescription>
            {templates.length} modèle(s) enregistré(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun modèle enregistré</p>
              <p className="text-sm text-slate-400 mt-1">
                Créez votre premier modèle pour gagner du temps
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <div 
                  key={template.id}
                  className="p-4 border border-slate-200 rounded-xl hover:border-blue-300 transition-all"
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-slate-800 flex items-center gap-2">
                        {template.name}
                        {template.is_favorite && (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        )}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {template.items?.length || 0} ligne(s) • {calculateTotal(template.items || []).toFixed(0)}€ TTC
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFavorite(template)}
                      className="text-slate-400 hover:text-amber-500"
                    >
                      <Star className={`h-4 w-4 ${template.is_favorite ? 'text-amber-500 fill-amber-500' : ''}`} />
                    </Button>
                  </div>
                  
                  {template.category && (
                    <Badge variant="outline" className="mb-3 text-xs">
                      {template.category}
                    </Badge>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-slate-400">
                      Utilisé {template.use_count || 0} fois
                    </span>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(template)}
                        className="text-slate-500 hover:text-blue-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-slate-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceTemplatesPage;
